// One command to MANY Bluetooth devices — the BLE Test screen's batch mode
// (owner, 2026-10-18: "10 devices, same command, check boxes, select all").
//
// ⚠️ ONE AT A TIME, NEVER IN PARALLEL. Each device is its own authenticated link
// (core/ble/bleSession: connect → notifications → AUTH → command), and Android
// stacks fail silently on concurrent GATT operations (docs/13). So the batch
// walks the list: open, send, wait for the device's answer, close, next.
//
// ⚠️ A FAILED AUTH IS NEVER RETRIED — three failures and the device drops the
// link (bleSession). Each device gets exactly one attempt; the result says why
// it failed, and the operator re-runs only the failed ones.
//
// The commands are the SAME strings the app sends over MQTT — the firmware runs
// BLE and MQTT through one executor (ESP8685WROOM04C 1.1.3+) — except Add Wi-Fi,
// which uses the provisioning form (`WIFI:` / credentials, bleConfig
// chooseWifiPayload) that every BLE firmware accepts. A Wi-Fi change REBOOTS the
// device, so its link dropping right after the command counts as done.

import { openSession } from './bleSession';
import { stopScan } from './bleClient';
import {
  buildAllRelays, chooseWifiPayload, isProvisionFailure, isProvisionSuccess, CMD_STATUS,
} from './bleConfig';
import { buildRemoveWifiMqtt } from '../devices/commands';

export const BATCH_REPLY_MS = 5000;   // how long to wait for each device to answer
const GAP_MS = 400;                   // between devices: let the stack release the link

/**
 * key, label, the fields it needs, and how to build the wire text.
 * build(fields) -> { ok: true, text } | { ok: false, error }
 */
export const BATCH_COMMANDS = [
  { key: 'allon', label: 'All On', fields: [], build: () => ({ ok: true, text: buildAllRelays(true) }) },
  { key: 'alloff', label: 'All Off', fields: [], build: () => ({ ok: true, text: buildAllRelays(false) }) },
  {
    key: 'addwifi',
    label: 'Add Wi-Fi',
    fields: ['ssid', 'pass'],
    reboots: true,
    build: ({ ssid, pass }) => {
      const r = chooseWifiPayload(ssid, pass);
      return r.ok ? { ok: true, text: r.payload } : r;
    },
  },
  {
    key: 'removewifi',
    label: 'Remove Wi-Fi',
    fields: ['ssid', 'pass'],
    reboots: true,
    build: ({ ssid, pass }) => (String(ssid || '').trim()
      ? { ok: true, text: buildRemoveWifiMqtt(String(ssid).trim(), String(pass ?? '')) }
      : { ok: false, error: 'Enter the Wi-Fi name to remove.' }),
  },
  { key: 'status', label: 'Status', fields: [], build: () => ({ ok: true, text: CMD_STATUS }) },
  {
    key: 'custom',
    label: 'Custom command',
    fields: ['text'],
    build: ({ text }) => (String(text || '').trim()
      ? { ok: true, text: String(text).trim() }
      : { ok: false, error: 'Type the command to send.' }),
  },
];

export const batchCommand = (key) => BATCH_COMMANDS.find((c) => c.key === key) || null;

const sleep = (ms) => new Promise((r) => { setTimeout(r, ms); });

/**
 * Send one command to each device in turn.
 *
 * @param devices   [{ deviceId, uniqueNumber }] — scan rows
 * @param command   one of BATCH_COMMANDS
 * @param fields    the command's inputs ({ ssid, pass } / { text })
 * @param opts.onProgress (results) — called after every device with all results so far
 * @param opts.shouldStop () => bool — checked between devices (a Stop button)
 * -> results: [{ uniqueNumber, ok, reply, error }]
 */
export async function runBleBatch(devices, command, fields = {}, opts = {}) {
  const { onProgress = null, shouldStop = () => false, replyMs = BATCH_REPLY_MS } = opts;
  const built = command && command.build(fields);
  if (!built || !built.ok) throw new Error((built && built.error) || 'Unknown command');

  const results = [];
  const push = (r) => { results.push(r); if (onProgress) onProgress([...results]); };

  await stopScan();
  // eslint-disable-next-line no-restricted-syntax
  for (const d of devices) {
    if (shouldStop()) {
      push({ uniqueNumber: d.uniqueNumber, ok: false, error: 'Stopped before this device' });
      // eslint-disable-next-line no-continue
      continue;
    }
    if (!d.uniqueNumber) {
      push({ uniqueNumber: d.name || d.deviceId, ok: false, error: 'No unique number in its name — cannot authenticate' });
      // eslint-disable-next-line no-continue
      continue;
    }

    let answer = null;
    let dropped = false;
    let wake = null;
    // eslint-disable-next-line no-await-in-loop
    const opened = await openSession(d.deviceId, d.uniqueNumber, {
      onStatus: (text) => { if (answer == null) { answer = String(text); if (wake) wake(); } },
      onDisconnect: () => { dropped = true; if (wake) wake(); },
    });
    if (!opened.ok) {
      push({
        uniqueNumber: d.uniqueNumber,
        ok: false,
        error: opened.code === 'OWNER_SECRET_MISSING'
          ? 'Owned device — needs the owner\'s proof (not sent from the bench)'
          : (opened.error || opened.code || 'Could not connect'),
      });
      // eslint-disable-next-line no-await-in-loop
      await sleep(GAP_MS);
      // eslint-disable-next-line no-continue
      continue;
    }

    const { session } = opened;
    try {
      // eslint-disable-next-line no-await-in-loop
      await session.send(built.text);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        wake = resolve;
        if (answer != null || dropped) resolve();
        setTimeout(resolve, replyMs);
      });
      wake = null;
      if (command.reboots) {
        // A Wi-Fi change: "SAVED"/the link dropping (it reboots) = done.
        if (answer && isProvisionFailure(answer)) {
          push({ uniqueNumber: d.uniqueNumber, ok: false, reply: answer, error: 'Device rejected it' });
        } else if ((answer && isProvisionSuccess(answer)) || dropped || answer) {
          push({ uniqueNumber: d.uniqueNumber, ok: true, reply: answer || 'restarting (link dropped)' });
        } else {
          push({ uniqueNumber: d.uniqueNumber, ok: false, error: 'No answer' });
        }
      } else if (answer != null) {
        push({ uniqueNumber: d.uniqueNumber, ok: true, reply: answer });
      } else {
        push({ uniqueNumber: d.uniqueNumber, ok: false, error: dropped ? 'Link dropped' : 'Sent — no answer' });
      }
    } catch (e) {
      push({ uniqueNumber: d.uniqueNumber, ok: false, error: (e && e.message) || 'Write failed' });
    } finally {
      // eslint-disable-next-line no-await-in-loop
      await session.close().catch(() => {});
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(GAP_MS);
  }
  return results;
}
